import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Badge, Input, Button } from "@/components/ui-kit";
import { MessageCircle, UserCheck, Send, Search, RefreshCw, Inbox } from "lucide-react";

function formatTs(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit", hour12: false }); } catch { return ""; }
}
function formatDateTs(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short" }); } catch { return ""; }
}

/**
 * AgentInbox — full-screen admin view of all 'agent' kind chat threads from clients.
 * Item 1b iter v20 — optional companion to the floating ChatAgentPopout.
 */
export default function AgentInbox() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      // Iter v24 #5 — unified inbox: use the same endpoint as the floating CS popout
      // so both views stay in sync (CS, agent, and client_chat kinds combined).
      const r = await api.get("/chat/cs/threads");
      setThreads(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat inbox"); }
    finally { setLoading(false); }
  }, []);

  const loadMessages = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const r = await api.get(`/chat/threads/${tid}/messages`);
      setMessages(r.data);
      await api.post(`/chat/threads/${tid}/read`);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 10000);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => {
    if (!activeId) return;
    const id = setInterval(() => loadMessages(activeId), 5000);
    return () => clearInterval(id);
  }, [activeId, loadMessages]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return threads;
    return threads.filter((t) => (t.client_name || t.name || "").toLowerCase().includes(s));
  }, [threads, search]);

  const totalUnread = useMemo(() => threads.reduce((s, t) => s + (t.unread_count || 0), 0), [threads]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!activeId || !body.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${activeId}/messages`, { body: body.trim() });
      setBody("");
      await loadMessages(activeId);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim"); }
    finally { setSending(false); }
  };

  const grouped = useMemo(() => {
    const out = []; let last = "";
    for (const m of messages) {
      const d = formatDateTs(m.ts);
      if (d !== last) { out.push({ type: "date", key: "d-" + d, date: d }); last = d; }
      out.push({ type: "msg", key: m.id, msg: m });
    }
    return out;
  }, [messages]);

  const active = threads.find((t) => t.id === activeId);

  return (
    <div className="max-w-[1500px] mx-auto p-4 md:p-6 lg:p-8 space-y-5">
      <PageHeader
        overline="Admin"
        title="Inbox Chat (Agen & CS)"
        description="Semua percakapan klien — chat ke agen maupun ke Customer Service — terkonsolidasi di sini. Sinkron dengan widget chat di pojok layar."
        action={
          <div className="flex items-center gap-2">
            {totalUnread > 0 && <Badge tone="warn">{totalUnread} belum dibaca</Badge>}
            <Button onClick={refresh} variant="secondary" className="!text-xs"><RefreshCw className="h-3 w-3" />Refresh</Button>
          </div>
        }
      />
      <Card className="p-0 overflow-hidden" data-testid="agent-inbox">
        <div className="grid grid-cols-1 lg:grid-cols-[340px,1fr] min-h-[640px]">
          {/* Thread list */}
          <div className="border-r border-stone-200 flex flex-col">
            <div className="p-3 border-b border-stone-200">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari klien…" className="!pl-8 !text-sm" data-testid="agent-inbox-search" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="agent-inbox-threads">
              {loading ? (
                <p className="p-8 text-center text-xs text-stone-400">Memuat…</p>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-stone-400">
                  <Inbox className="h-6 w-6 mx-auto mb-2 text-stone-300" />
                  <p className="text-xs">Belum ada chat dari klien.</p>
                </div>
              ) : (
                filtered.map((t) => {
                  const last = t.last_message?.deleted ? "(pesan dihapus)" : (t.last_message?.body || "");
                  const isActive = t.id === activeId;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActiveId(t.id)}
                      className={`w-full text-left px-3 py-3 border-b border-stone-100 flex items-start gap-2.5 transition-colors ${isActive ? "bg-blue-50/70" : "hover:bg-stone-50/60"}`}
                      data-testid={`agent-inbox-thread-${t.id}`}
                    >
                      <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xs font-display font-bold shrink-0">
                        {(t.client_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="flex-1 truncate text-sm font-medium text-stone-900">{t.client_name || t.name}</p>
                          {t.unread_count > 0 && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-mono">{t.unread_count}</span>}
                        </div>
                        {last && <p className="text-[11px] text-stone-500 truncate mt-0.5">{last}</p>}
                        <p className="text-[10px] text-stone-400 font-mono mt-0.5">{formatDateTs(t.last_message_at)} {formatTs(t.last_message_at)}</p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
          {/* Chat panel */}
          <div className="flex flex-col">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-stone-400">
                <div className="text-center">
                  <MessageCircle className="h-10 w-10 mx-auto mb-3 text-stone-300" />
                  <p className="text-sm">Pilih percakapan di sebelah kiri untuk mulai membalas.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-stone-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-blue-700" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-900 truncate">{active.client_name || active.name}</p>
                    <p className="text-[10px] uppercase tracking-widest text-blue-700">Chat Agen · Klien</p>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3 bg-stone-50/30 space-y-1.5" data-testid="agent-inbox-messages">
                  {grouped.length === 0 && <p className="text-center text-xs text-stone-400 py-6">Belum ada pesan.</p>}
                  {grouped.map((g) => {
                    if (g.type === "date") return (
                      <div key={g.key} className="text-center my-2">
                        <span className="text-[10px] uppercase tracking-widest text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{g.date}</span>
                      </div>
                    );
                    const m = g.msg;
                    const mine = m.sender_id === user?.id;
                    return (
                      <div key={g.key} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-blue-600 text-white" : "bg-white border border-stone-200 text-stone-900"} ${m.deleted ? "italic opacity-50" : ""}`}>
                          {!mine && !m.deleted && <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 mb-0.5">{m.sender_name}</div>}
                          {m.deleted ? <span className="text-xs">(pesan dihapus)</span> : <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                          <div className={`text-[10px] font-mono mt-1 ${mine ? "text-white/70 text-right" : "text-stone-400"}`}>{formatTs(m.ts)}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <form onSubmit={submit} className="border-t border-stone-200 p-3 bg-white">
                  <div className="flex items-end gap-2">
                    <Input value={body} onChange={(e) => setBody(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }}
                      placeholder={`Balas pesan dari ${active.client_name || "klien"}…`}
                      className="flex-1 !text-sm" data-testid="agent-inbox-input" />
                    <Button type="submit" disabled={sending || !body.trim()} className="!bg-blue-600 hover:!bg-blue-700" data-testid="agent-inbox-send">
                      <Send className="h-3.5 w-3.5" />Kirim
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
